import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, IconButton, CircularProgress, Tooltip, Typography, Pagination,
  Dialog, DialogContent, DialogTitle
} from '@mui/material';
import CommentOutlined from '@mui/icons-material/CommentOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getLeadDashboard } from '../../../Redux/actions';
import { updateParams } from '../../../utils';
import { paginationStyle, datePickerSlotProps } from '../../../theme/formStyles';
import SearchBox from '../../Common/SearchBox';
import ImagePlaceholder from '../../../Images/image-placeholder.png';

const validateToDate = (fromDate, toDate) =>
  fromDate && toDate && toDate < fromDate
    ? "'To Date' must be equal to or later than 'From Date'"
    : '';

const getStageStyle = (stage) => {
  switch (stage) {
    case 'Moved to Qualified Lead':
      return { color: '#196a3e', bgcolor: '#f1faf4', border: '1px solid #c2e9cf' };
    case 'Met':
      return { color: '#1a6894', bgcolor: '#f1f8fc', border: '1px solid #b8ddec' };
    case 'Contacted':
      return { color: '#8f6d1d', bgcolor: '#fffdee', border: '1px solid #eed699' };
    default:
      return { color: '#757575', bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' };
  }
};

const getRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';
  const diff = new Date() - new Date(dateString);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const itemsPerPage = 20;

export default function LeadList({ onCreate, onEdit, refreshKey }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const existingParams = Object.fromEntries([...queryParams]);

  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [totalPages, setTotalPages] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [createdFromDate, setCreatedFromDate] = useState(existingParams?.createdFromDate || '');
  const [createdToDate, setCreatedToDate] = useState(existingParams?.createdToDate || '');
  const [dateError, setDateError] = useState('');
  const [selectedCardUrl, setSelectedCardUrl] = useState('');
  const [openCardDialog, setOpenCardDialog] = useState(false);

  useEffect(() => {
    if (existingParams?.page) {
      return;
    }
    updateQueryParams({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, refreshKey]);

  useEffect(() => {
    setCreatedFromDate(existingParams?.createdFromDate || '');
    setCreatedToDate(existingParams?.createdToDate || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const fetchLeads = () => {
    setLoading(true);
    const params = {
      page: existingParams?.page || 1,
      search: existingParams?.search || '',
      createdFromDate: existingParams?.createdFromDate || '',
      createdToDate: existingParams?.createdToDate || '',
    };
    dispatch(getLeadDashboard(params)).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        setLeads(res.data.data || []);
        setTotalPages(res.data.totalPages);
        setTotalCount(res.data.totalItems);
        setCurrentPage(res.data.currentPage);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const updateQueryParams = (params) => {
    const searchParams = updateParams(existingParams, params);
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const handlePageChange = (event, newPage) => {
    updateQueryParams({ page: newPage });
  };

  const handleSearch = (value) => {
    updateQueryParams({ search: value, page: 1 });
  };

  const handleApplyDateFilter = () => {
    const error = validateToDate(createdFromDate, createdToDate);
    if (error) {
      setDateError(error);
      return;
    }
    updateQueryParams({ createdFromDate, createdToDate, page: 1 });
  };

  const handleClearDateFilter = () => {
    setCreatedFromDate('');
    setCreatedToDate('');
    setDateError('');
    updateQueryParams({ createdFromDate: '', createdToDate: '', page: 1 });
  };

  const handleCardClick = (url) => {
    setSelectedCardUrl(url);
    setOpenCardDialog(true);
  };

  const handleCloseCardDialog = () => {
    setOpenCardDialog(false);
    setSelectedCardUrl('');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          onClick={onCreate}
          sx={{ backgroundColor: '#008080', '&:hover': { backgroundColor: '#006666' }, textTransform: 'none' }}
        >
          + Create lead
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', pr: 1 }}>Search By:</Typography>
            <SearchBox
              value={existingParams?.search || ''}
              placeholder="Client / Opportunity / Facility"
              search={handleSearch}
            />
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <DatePicker
                label="From Date"
                format="DD/MM/YYYY"
                value={createdFromDate ? dayjs(createdFromDate) : null}
                onChange={(newVal) => {
                  const formatted = newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : '';
                  setCreatedFromDate(formatted);
                  setDateError(validateToDate(formatted, createdToDate));
                }}
                slotProps={datePickerSlotProps({ size: 'small' })}
              />
              <DatePicker
                label="To Date"
                format="DD/MM/YYYY"
                value={createdToDate ? dayjs(createdToDate) : null}
                minDate={createdFromDate ? dayjs(createdFromDate) : undefined}
                onChange={(newVal) => {
                  const formatted = newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : '';
                  setCreatedToDate(formatted);
                  setDateError(validateToDate(createdFromDate, formatted));
                }}
                slotProps={datePickerSlotProps({ size: 'small', error: !!dateError, helperText: dateError })}
              />
              <Button
                size="small"
                variant="contained"
                onClick={handleApplyDateFilter}
                disabled={!!dateError || (!createdFromDate && !createdToDate)}
                sx={{ backgroundColor: '#008080', '&:hover': { backgroundColor: '#006666' }, textTransform: 'none' }}
              >
                Apply
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearDateFilter}
                disabled={!createdFromDate && !createdToDate}
                sx={{ borderColor: '#008080', color: '#008080', textTransform: 'none' }}
              >
                Clear
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #eaeaea' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['SI No', 'Client Name', 'Facility', 'Location', 'Opportunity Name', 'Segment', 'Stage', 'BD Owner', 'Contact', 'Created Date', 'Pipeline Days', 'Last Updated', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} sx={{ color: '#008080' }} />
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 3, color: '#888' }}>
                    No Data Found
                  </TableCell>
                </TableRow>
              ) : leads.map((lead, index) => {
                const pipelineDays = Math.floor((new Date() - new Date(lead.created_at)) / (1000 * 60 * 60 * 24));
                const bdOwnerNames = (lead.bd_owners || []).map(bd => bd.name).join(', ') || '-';
                const stageComments = (lead.stage_updates || []).filter(s => s.comment);
                return (
                  <TableRow
                    key={lead.id}
                    onMouseEnter={() => setHoveredRowId(lead.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    sx={{ '& td': { borderBottom: '1px solid #f0f0f0', py: 1.2 } }}
                  >
                    <TableCell>{(currentPage - 1) * itemsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{lead.client_name}</TableCell>
                    <TableCell>{lead.facility_name || '-'}</TableCell>
                    <TableCell>{lead.location || '-'}</TableCell>
                    <TableCell>{lead.opportunity_name}</TableCell>
                    <TableCell>{lead.segment || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip label={lead.current_stage} size="small" sx={{ fontWeight: 500, ...getStageStyle(lead.current_stage) }} />
                        {stageComments.length > 0 && (
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                {stageComments.map((item, idx) => (
                                  <Box key={idx} sx={{ mb: idx < stageComments.length - 1 ? 1.5 : 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#ffd700', display: 'block', mb: 0.5 }}>
                                      {item.stage}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                                      {item.comment}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            }
                            arrow
                            placement="right"
                          >
                            <CommentOutlined sx={{ fontSize: '1rem', color: '#666', cursor: 'pointer', '&:hover': { color: '#008080' } }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{bdOwnerNames}</TableCell>
                    <TableCell>
                      {lead.contact_person ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{lead.contact_person}</span>
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                  <b>Designation:</b> {lead.designation || '-'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                  <b>Email:</b> {lead.contact_email || '-'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  <b>Mobile:</b> {lead.contact_mobile || '-'}
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="right"
                          >
                            <PersonOutlineIcon sx={{ fontSize: '1rem', color: '#666', cursor: 'pointer', '&:hover': { color: '#008080' } }} />
                          </Tooltip>
                          {lead.visiting_card_url && (
                            <img
                              src={ImagePlaceholder}
                              alt="Visiting card"
                              style={{ cursor: 'pointer', maxWidth: '18px' }}
                              onClick={() => handleCardClick(lead.visiting_card_url)}
                            />
                          )}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                    <TableCell>{pipelineDays} days</TableCell>
                    <TableCell>{getRelativeTime(lead.updated_at)}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(lead.id)}
                          sx={{ opacity: hoveredRowId === lead.id ? 1 : 0 }}
                        >
                          <EditIcon fontSize="small" sx={{ color: 'black' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {leads.length > 0 && (
        <Grid container item xs={12} style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
          <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '24px' }}>
            <span style={{ fontFamily: '"Helvetica Neue"', fontSize: '13px', color: '#3d3d3d' }}>
              {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}-
              {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}
            </span>
          </Grid>
          <Pagination
            page={currentPage}
            count={totalPages}
            variant="outlined"
            shape="rounded"
            sx={paginationStyle}
            onChange={handlePageChange}
          />
        </Grid>
      )}

      <Dialog open={openCardDialog} onClose={handleCloseCardDialog}>
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseCardDialog}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'gray', zIndex: 100 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="img" src={selectedCardUrl} alt="Visiting card" sx={{ maxWidth: '100%', maxHeight: '70vh' }} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
